import { useCallback } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFieldArray } from '@hookform/lenses/rhf';
import { z } from 'zod/v4';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Button } from '@interxfi/design-system/components/button/button';
import { PlainIconButton } from '@interxfi/design-system/components/button/plain-icon/plain-icon-button';
import { Typography } from '@interxfi/design-system/components/typography';
import DeleteIcon from '@interxfi/design-system/icons/react/icon-delete';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import type { IxcRegistrationV1TrustedPerson } from '@interxfi/types/generated/api';
import { AppTextInputWithLens } from '../../../../../components/form/app-text-input';
import { getOptionalSchema, OptionalField, type OptionalFieldWrapper } from '../../../../../components/form/optional-field';
import { addDefinedValues } from '../../../../../util/has-value';
import {
  AddressForm,
  type AddressFormValues,
  addressFormSchema,
  defaultAddressFormValues,
  formValuesToAddress,
  getInitialFormValuesForAddress,
} from '../../../shared-form/address/address-form';
import {
  defaultNameFormValues,
  formValuesToRegistrationName,
  getInitialFormValuesForRegistrationName,
  NameForm,
  nameFormSchema,
} from '../../../shared-form/name-form';
import { formValuesToPhones, getInitialFormValuesForPhones, PhonesForm, phoneFormSchema } from '../../../shared-form/phone-form';

export const trustedPersonSchema = z.object({
  name: nameFormSchema,
  email: z.email({ error: 'Email address must be valid' }).or(z.literal('')),
  phone: z.array(phoneFormSchema),
  address: getOptionalSchema(addressFormSchema),
  relationship: z.string(),
});

export type TrustedPersonFormValues = z.infer<typeof trustedPersonSchema>;

export const trustedPersonsFormSchema = z.array(trustedPersonSchema);

export type TrustedPersonsFormValues = z.infer<typeof trustedPersonsFormSchema>;

export const defaultTrustedPersonFormValues: TrustedPersonFormValues = {
  name: { ...defaultNameFormValues },
  email: '',
  phone: [],
  address: {
    isProviding: false,
    value: undefined,
  },
  relationship: '',
};

export function getInitialFormValuesForTrustedPerson(trustedPerson: IxcRegistrationV1TrustedPerson | undefined): TrustedPersonFormValues | undefined {
  if (!trustedPerson) {
    return undefined;
  }

  return addDefinedValues<TrustedPersonFormValues>(
    {
      ...defaultTrustedPersonFormValues,
    },
    {
      name: getInitialFormValuesForRegistrationName(trustedPerson.name),
      email: trustedPerson.email,
      phone: getInitialFormValuesForPhones(trustedPerson.phone),
      address: {
        isProviding: Boolean(trustedPerson.address),
        value: trustedPerson.address ? getInitialFormValuesForAddress(trustedPerson.address) : undefined,
      },
      relationship: trustedPerson.relationship,
    },
  );
}

export function getInitialFormValuesForTrustedPersons(
  trustedPersons: IxcRegistrationV1TrustedPerson[] | undefined,
): TrustedPersonsFormValues | undefined {
  if (!trustedPersons?.length) {
    return undefined;
  }

  return trustedPersons.reduce<TrustedPersonsFormValues>((acc, curr) => {
    const trustedPerson = getInitialFormValuesForTrustedPerson(curr);

    if (trustedPerson) {
      acc.push(trustedPerson);
    }

    return acc;
  }, []);
}

export function formValuesToTrustedPerson(person: TrustedPersonFormValues): IxcRegistrationV1TrustedPerson | undefined {
  return addDefinedValues<IxcRegistrationV1TrustedPerson>(
    {
      name: formValuesToRegistrationName(person.name),
    },
    {
      email: person.email,
      phone: formValuesToPhones(person.phone),
      address: person.address.isProviding && person.address.value ? formValuesToAddress(person.address.value) : undefined,
      relationship: person.relationship,
    },
  );
}

export function formValuesToTrustedPersons(persons: TrustedPersonsFormValues): IxcRegistrationV1TrustedPerson[] | undefined {
  if (!persons.length) {
    return undefined;
  }

  return persons.reduce<IxcRegistrationV1TrustedPerson[]>((acc, curr) => {
    const trustedPerson = formValuesToTrustedPerson(curr);

    if (trustedPerson) {
      acc.push(trustedPerson);
    }

    return acc;
  }, []);
}

const nameColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
  lg: 3,
};

interface TrustedPersonFormProps {
  lens: Lens<TrustedPersonFormValues>;
}

function TrustedPersonForm({ lens }: TrustedPersonFormProps) {
  return (
    <VerticalStack gap={4}>
      <NameForm columns={nameColumns} lens={lens.focus('name')} />
      <AppTextInputWithLens label="Relationship" lens={lens.focus('relationship')} />
      <AppTextInputWithLens autoComplete="off" label="Email Address" lens={lens.focus('email')} />
      <PhonesForm labelPlaceholder="Home, Mobile..." lens={lens.focus('phone')} />

      <OptionalField<AddressFormValues>
        defaultValue={defaultAddressFormValues}
        label="Provide Address"
        lens={lens.focus('address').narrow<OptionalFieldWrapper<AddressFormValues>>()}
      >
        <AddressForm lens={lens.focus('address.value').defined()} />
      </OptionalField>
    </VerticalStack>
  );
}

interface TrustedPersonsFormProps {
  lens: Lens<TrustedPersonsFormValues>;
}

export function TrustedPersonsForm({ lens }: TrustedPersonsFormProps) {
  const { append, fields, remove } = useFieldArray(lens.interop());

  const handleAppendPhone = useCallback(() => {
    append({ ...defaultTrustedPersonFormValues });
  }, [append]);

  return (
    <VerticalStack gap={7}>
      {lens.map(fields, (arrayField, l, i) => (
        <VerticalStack gap={7} key={arrayField.id}>
          <HorizontalStack blockAlign="center" gap={4} wrap="nowrap">
            <PlainIconButton
              aria-label={`Remove trusted contact person #${i + 1}`}
              onClick={() => {
                remove(i);
              }}
              tone="danger-dk"
            >
              <DeleteIcon aria-hidden height={16} />
            </PlainIconButton>

            <Typography.Level3>Trusted Contact Person</Typography.Level3>
          </HorizontalStack>

          <TrustedPersonForm lens={l} />
        </VerticalStack>
      ))}

      <Button affixIcon={<PlusIcon aria-hidden />} onClick={handleAppendPhone} size="medium" variant="light">
        Add a Trusted Contact Person
      </Button>
    </VerticalStack>
  );
}
